import React, { useState } from 'react';
import {
    Grid,
    Collapse,
    Box,
    Stack,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Alert,
    
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Select, DateInput, InformationAlert, OnOffSwitch, BinaryButtonGroup } from '../../Common';
import Enums from '../../../enums';
import { testResultsOptions } from '../utils';
import { Translate, TranslateW } from "../../../Utils/Languages/Translate";
import { lightRed, red } from '../../../color_utils';

const InitialVertificationTest = ({
    formState,
    formState: {
        workItemTypeId: serviceEventTypeId,
        initialLeakInspection = {},
        initialVerificationLeakInspection = {},
    },
    setGlobalState,
    dropdownOptions,
    requiredFieldsMissing: {
        initialVerificationLeakInspection: errors = {},
    },
    setRequiredFieldsMissing,
    serviceEventSteps,
    setServiceEventSteps,
    setRequiredFields,
    fieldsWithErrors: {
        repairDate: {
            errorExists = false,
            helperText = "",
        } = {}
    },
    requiredFieldsRef,
    skippedStepIds,
    setSkippedStepIds,
}) => {
    const isNestedProperty = true;
    const isServiceOrLeakRepair = serviceEventTypeId === Enums.SE.WorkItemType.ServiceOrLeakRepair;
    const hideIvtFields = formState.repairOutcomeTypeId === Enums.SE.RepairOutcomeType.NoRepairAttempted;
    const [confirmClearGasOpen, setConfirmClearGasOpen] = useState(false);

    const handleLeakTestResult = (leakTestResult) => {
        let newSteps = [...serviceEventSteps];
        const mappedNewSteps = serviceEventSteps.map(s => s.props.id);
        let finalRequiredFields = requiredFieldsRef.current;

        // Add follow-up verification when the initial verification fails.
        if (leakTestResult === true) {
            if (!mappedNewSteps.includes('FollowUpVerificationService')) {
                // Insert right BEFORE Completion so Completion always stays last.
                // (The unified layout has 6 steps; the old hardcoded index 6 landed
                // AFTER Completion, pushing Follow Up Verification past it.)
                const completionIdx = newSteps.findIndex(s => s.props.id === 'Completion');
                newSteps.splice(completionIdx === -1 ? newSteps.length : completionIdx, 0, Translate('FollowUpVerificationService'));
            }
            delete formState.initialVerificationLeakInspection.inspectionDate;

            finalRequiredFields = {
                ...finalRequiredFields,
                "InitialVerificationTest": [
                    "leakDetectionMethodId",
                    "wasLeakFound",
                ],
                "FollowUpVerificationService": [
                    "inspectionDate",
                    "leakDetectionMethodId",
                    "wasLeakFound",
                ],
            };

            // RepairDetails leak found = true
            // && InitialVerificationTest leak test result = fail (leak found = true)
            if (
                formState.initialLeakInspection.wasLeakFound
                && leakTestResult === true
            ) {
                finalRequiredFields = {
                    ...finalRequiredFields,
                    "FollowUpVerificationService": [
                        ...finalRequiredFields["FollowUpVerificationService"],
                        "leakRepairDispositionTypeId",
                    ]
                };
            }
        }

        // Remove follow-up verification when the initial verification passes.
        if (leakTestResult === false && mappedNewSteps.includes('FollowUpVerificationService')) {
            const followIdx = newSteps.findIndex(s => s.props.id === 'FollowUpVerificationService');
            if (followIdx !== -1) newSteps.splice(followIdx, 1);
            delete formState.dateOfFollowUpService;
            delete formState.verificationLeakInspection.leakRepairDispositionTypeId;
            delete formState.verificationLeakInspection.leakDetectionMethodId;
            delete formState.verificationLeakInspection.wasLeakFound;
            delete formState.verificationLeakInspection.leakLocationCategoryId;
            delete formState.verificationLeakInspection.leakLocationId;
            delete formState.verificationLeakInspection.faultCauseTypeId;
            delete formState.verificationLeakInspection.estimatedLeakAmount;
            delete formState.verificationLeakInspection.notes;
            delete formState.verificationLeakInspection.inspectionDate;

            finalRequiredFields = {
                ...finalRequiredFields,
                "InitialVerificationTest": [
                    "leakDetectionMethodId",
                    "wasLeakFound",
                    "inspectionDate",
                ]
            };
        }
        if (formState.assetStatusId === 1) {
            const normalOperationId = dropdownOptions.afterServiceStatusOptions[0].id;
            setGlobalState("finalCoolingApplianceStatusId", normalOperationId);
        }
        setGlobalState("initialVerificationLeakInspection.wasLeakFound", leakTestResult, isNestedProperty);
        requiredFieldsRef.current = finalRequiredFields;
        setRequiredFields(finalRequiredFields);
        return setServiceEventSteps(newSteps);
    };

    const applyGasTransferredOff = () => {
        // Keep GasTransaction in serviceEventSteps but mark it as skipped so the navigator
        // shows it grayed/dashed with a "Skipped" label per the AC.
        const finalRequiredFields = { ...requiredFieldsRef.current };
        delete finalRequiredFields.GasTransaction;

        setGlobalState('recoveries', []);
        setGlobalState('installs', []);
        if (setRequiredFieldsMissing) {
            setRequiredFieldsMissing(prev => {
                const next = { ...prev };
                delete next.GasTransaction;
                return next;
            });
        }
        if (setSkippedStepIds) {
            setSkippedStepIds(prev =>
                prev.includes('GasTransaction') ? prev : [...prev, 'GasTransaction']
            );
        }

        setGlobalState('wasGasTransferred', false);
        requiredFieldsRef.current = finalRequiredFields;
        setRequiredFields(finalRequiredFields);
    };

    const applyGasTransferredOn = () => {
        // Restore GasTransaction to a non-skipped, fully active step. Re-insert into the array
        // if a prior code path removed it (legacy paths predate skipped-state tracking).
        const stepIds = serviceEventSteps.map(s => s.props.id);
        if (!stepIds.includes('GasTransaction')) {
            const newSteps = [...serviceEventSteps];
            // GasTransaction sits AFTER InitialVerificationTest in the default order.
            const ivtIdx = newSteps.findIndex(s => s.props.id === 'InitialVerificationTest');
            let insertAt = ivtIdx >= 0
                ? ivtIdx + 1
                : newSteps.findIndex(s => s.props.id === 'Completion');
            if (insertAt < 0) insertAt = newSteps.length;
            newSteps.splice(insertAt, 0, Translate('GasTransaction'));
            setServiceEventSteps(newSteps);
        }
        const finalRequiredFields = {
            ...requiredFieldsRef.current,
            GasTransaction: [],
        };
        if (setSkippedStepIds) {
            setSkippedStepIds(prev => prev.filter(id => id !== 'GasTransaction'));
        }

        setGlobalState('wasGasTransferred', true);
        requiredFieldsRef.current = finalRequiredFields;
        setRequiredFields(finalRequiredFields);
    };

    const handleWasGasTransferredChange = (newValue) => {
        if (newValue === false) {
            const hasGasData = (
                (formState.recoveries && formState.recoveries.length > 0)
                || (formState.installs && formState.installs.length > 0)
            );
            if (hasGasData) {
                setConfirmClearGasOpen(true);
                return;
            }
            applyGasTransferredOff();
        } else {
            applyGasTransferredOn();
        }
    };

    const handleConfirmClearGasContinue = () => {
        setConfirmClearGasOpen(false);
        applyGasTransferredOff();
    };

    const handleConfirmClearGasCancel = () => {
        setConfirmClearGasOpen(false);
    };

    const wasGasTransferredValue = formState.wasGasTransferred ?? true;
    return (
        <Grid container spacing={4} justifyContent='flex-start'>
            {!hideIvtFields && (
                <>
                    <Grid item xs={12} md={6}>
                        <Select
                            required
                            label={TranslateW("leakInspectionView.leak-detection-method") }
                            value={formState.initialVerificationLeakInspection.leakDetectionMethodId}
                            onChange={e => setGlobalState("initialVerificationLeakInspection.leakDetectionMethodId", e.target.value, isNestedProperty)}
                            options={dropdownOptions.leakDetectionMethods}
                            error={errors.leakDetectionMethodId}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <BinaryButtonGroup
                            id="initialVerificationLeakInspectionWasLeakFound"
                            required
                            label={TranslateW("leakRepair.verification-results")}
                            value={formState.initialVerificationLeakInspection.wasLeakFound}
                            onChange={selectedValue => handleLeakTestResult(selectedValue)}
                            options={testResultsOptions.map(option => ({
                                value: option.id,
                                label: option.name,
                                ...(option.id === true && {
                                    selectedColor: red,
                                    selectedBackgroundColor: lightRed,
                                }),
                            }))}
                            error={errors.wasLeakFound}
                            sx={{
                                '& .MuiToggleButtonGroup-root': {
                                    width: '100%',
                                    gap: '12px',
                                },
                                '& .MuiToggleButton-root': {
                                    flex: 1,
                                },
                            }}
                        />
                    </Grid>
                    {(formState.initialVerificationLeakInspection.wasLeakFound !== null
                        && formState.initialVerificationLeakInspection.wasLeakFound === false) && (
                        <>
                            <Grid item xs={12} md={6}>
                                <DateInput
                                    showTodayButton
                                    allowPast
                                    allowFuture={false}
                                    label={`${TranslateW("repairDate")}*`}
                                    date={formState.initialVerificationLeakInspection.inspectionDate}
                                    onChange={new_date => setGlobalState("initialVerificationLeakInspection.inspectionDate", new_date, isNestedProperty)}
                                    error={errors.inspectionDate || errorExists}
                                    customHelperText={helperText}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} />
                        </>
                    )}
                </>
            )}
            {hideIvtFields && (
                <Grid item xs={12}>
                    <Alert severity="info">
                        {TranslateW("serviceEventVerification.IvtNotRequiredNoRepair")}
                    </Alert>
                </Grid>
            )}
            {!hideIvtFields && formState.repairOutcomeTypeId === Enums.SE.RepairOutcomeType.LeakNotRepaired
                && formState.initialVerificationLeakInspection.wasLeakFound === true && (
                <Grid item xs={12} md={12} lg={12}>
                    <Alert severity="info">
                        {TranslateW("repairOutcome.SubGuidance.IvtFail")}
                    </Alert>
                </Grid>
            )}
            {!hideIvtFields && formState.repairOutcomeTypeId === Enums.SE.RepairOutcomeType.LeakNotRepaired
                && (formState.initialVerificationLeakInspection.wasLeakFound === null
                    || formState.initialVerificationLeakInspection.wasLeakFound === undefined) && (
                <Grid item xs={12} md={12} lg={12}>
                    <Alert severity="info">
                        {TranslateW("repairOutcome.SubGuidance.NoVerification")}
                    </Alert>
                </Grid>
            )}
            {isServiceOrLeakRepair && (
                <Grid item xs={12}>
                    <Box
                        sx={{
                            border: '1px dashed',
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderRadius: 1,
                            px: 3,
                            py: 2,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    Was Gas Transferred?
                                </Typography>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    sx={{ mt: 0.5 }}
                                >
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        sx={{ color: 'text.secondary' }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        Enabling this will add gas recovery/install reporting steps before final completion.
                                    </Typography>
                                </Stack>
                            </Box>
                            <OnOffSwitch
                                label=""
                                value={wasGasTransferredValue}
                                onChange={e => handleWasGasTransferredChange(e.target.checked)}
                            />
                        </Stack>
                    </Box>
                </Grid>
            )}
            <Dialog
                open={confirmClearGasOpen}
                onClose={handleConfirmClearGasCancel}
                aria-labelledby="clear-gas-transfers-title"
                aria-describedby="clear-gas-transfers-description"
            >
                <DialogTitle id="clear-gas-transfers-title">Clear gas transfers?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="clear-gas-transfers-description">
                        Changing to No will clear all saved gas transfers. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClearGasCancel}>Cancel</Button>
                    <Button onClick={handleConfirmClearGasContinue} variant="contained" autoFocus>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default InitialVertificationTest;
